import { useEffect, useRef, useCallback, useState } from 'react';
import throttle from 'lodash/throttle';

interface UseVisibilityRecoveryOptions {
  onVisible?: () => void;
  onHidden?: () => void;
  onFocus?: () => void;
  onNetworkOnline?: () => void;
}

const isBrowser =
  typeof window !== 'undefined' && typeof document !== 'undefined';

type LifecycleNavigator = Navigator & {
  readonly connection?: EventTarget;
  readonly standalone?: boolean;
};

/**
 * Reports browser lifecycle events so the wake lock can be reacquired after the
 * page returns. It never reconnects the socket: connection recovery belongs to
 * RoomSocketProvider's auto-connect.
 */
export const useVisibilityRecovery = ({
  onVisible,
  onHidden,
  onFocus,
  onNetworkOnline,
}: UseVisibilityRecoveryOptions) => {
  const onVisibleRef = useRef(onVisible);
  const onHiddenRef = useRef(onHidden);
  const onFocusRef = useRef(onFocus);
  const onNetworkOnlineRef = useRef(onNetworkOnline);

  // Track current visibility state
  const [isVisible, setIsVisible] = useState(
    isBrowser ? document.visibilityState === 'visible' : true
  );

  // Update refs when callbacks change
  useEffect(() => {
    onVisibleRef.current = onVisible;
    onHiddenRef.current = onHidden;
    onFocusRef.current = onFocus;
    onNetworkOnlineRef.current = onNetworkOnline;
  }, [onVisible, onHidden, onFocus, onNetworkOnline]);

  const handleVisibilityChange = useCallback(() => {
    const isCurrentlyVisible = document.visibilityState === 'visible';
    setIsVisible(isCurrentlyVisible);

    if (isCurrentlyVisible) {
      onVisibleRef.current?.();
    } else {
      onHiddenRef.current?.();
    }
  }, []);

  const handleFocus = useCallback(() => {
    onFocusRef.current?.();
  }, []);

  const handleBlur = useCallback(() => {
    onHiddenRef.current?.();
  }, []);

  const handleNetworkOnline = useCallback(() => {
    onNetworkOnlineRef.current?.();
  }, []);

  const handlePageHide = useCallback(() => {
    onHiddenRef.current?.();
  }, []);

  const handleOrientationChange = useCallback(() => {
    // Orientation change often indicates app returning to foreground on mobile
    onVisibleRef.current?.();
  }, []);

  const handleFreeze = useCallback(() => {
    // Page Lifecycle API: tab is being frozen by browser (mobile)
    onHiddenRef.current?.();
  }, []);

  const handleResume = useCallback(() => {
    // Page Lifecycle API: tab is being resumed from frozen state (mobile)
    onVisibleRef.current?.();
  }, []);

  const handleNetworkChange = useCallback(() => {
    // Network type change (WiFi ↔ cellular) - trigger health check
    onVisibleRef.current?.();
  }, []);

  const handleSystemWake = useCallback(() => {
    // Computer woke up from sleep - trigger health check
    onVisibleRef.current?.();
  }, []);

  // Optimized throttling: Critical events use leading=true for immediate response
  // Non-critical events use leading=false to reduce noise

  // CRITICAL: Immediate response for user returning to app
  const throttledHandleVisibilityChange = throttle(
    handleVisibilityChange,
    1000,
    {
      leading: true, // Immediate response when user returns
      trailing: true,
    }
  );

  const throttledHandleFocus = throttle(handleFocus, 500, {
    leading: true, // Immediate response for window focus
    trailing: true,
  });

  const throttledHandleResume = throttle(handleResume, 500, {
    leading: true, // Immediate response for mobile resume
    trailing: true,
  });

  const throttledHandleNetworkOnline = throttle(handleNetworkOnline, 1000, {
    leading: true, // Immediate response for network recovery
    trailing: true,
  });

  // NON-CRITICAL: Delayed response to reduce noise during transitions
  const throttledHandleBlur = throttle(handleBlur, 500, {
    leading: false, // Delay blur events to avoid rapid focus/blur cycles
    trailing: true,
  });

  const throttledHandlePageHide = throttle(handlePageHide, 500, {
    leading: false, // Delay navigation events
    trailing: true,
  });

  const throttledHandleFreeze = throttle(handleFreeze, 500, {
    leading: false, // Delay freeze events
    trailing: true,
  });

  // MODERATE: Balance between responsiveness and noise reduction
  const throttledHandleOrientationChange = throttle(
    handleOrientationChange,
    1000,
    {
      leading: false, // Orientation changes can be noisy
      trailing: true,
    }
  );

  const throttledHandleNetworkChange = throttle(
    handleNetworkChange,
    3000, // Longer throttle for connection type changes (can be very noisy)
    {
      leading: false,
      trailing: true,
    }
  );

  // Adaptive sleep/wake detection with intelligent idle management and battery optimization
  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    let lastTime = Date.now();
    let lastActivityTime = Date.now();
    let pageHiddenTime: number | null = null;
    let sleepCheckInterval: NodeJS.Timeout;
    let currentInterval = 30000; // Start with 30s intervals
    const maxInterval = 120000; // Max 2 minute intervals during extended idle
    const minInterval = 30000; // Min 30s intervals during active periods
    const pauseAfterHidden = 600000; // Pause sleep detection after 10 minutes hidden (battery optimization)

    const updateLastActivity = () => {
      lastActivityTime = Date.now();
      // Reset to more frequent checking when activity resumes
      if (currentInterval > minInterval) {
        clearTimeout(sleepCheckInterval);
        currentInterval = minInterval;
        scheduleNextCheck();
      }
    };

    const checkForSleep = () => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastTime;
      const idleTime = currentTime - lastActivityTime;

      // Battery optimization: Pause sleep detection if page hidden for extended period
      if (pageHiddenTime && currentTime - pageHiddenTime > pauseAfterHidden) {
        // Page has been hidden for 10+ minutes, pause sleep detection to save battery
        return; // Don't schedule next check
      }

      // If more than 60 seconds passed between checks, system likely slept
      if (timeDiff > 60000) {
        handleSystemWake();
        updateLastActivity(); // System wake counts as activity
      }

      lastTime = currentTime;

      // Adaptive interval based on idle time and visibility
      if (document.visibilityState === 'hidden') {
        // Slower checking when page is hidden
        currentInterval = Math.min(maxInterval, currentInterval * 2);
      } else if (idleTime > 300000) {
        // 5+ minutes idle
        currentInterval = Math.min(maxInterval, currentInterval * 1.5);
      } else if (idleTime < 60000) {
        // Less than 1 minute idle
        currentInterval = minInterval;
      }

      scheduleNextCheck();
    };

    const scheduleNextCheck = () => {
      sleepCheckInterval = setTimeout(checkForSleep, currentInterval);
    };

    const handleVisibilityForSleep = () => {
      if (document.visibilityState === 'hidden') {
        pageHiddenTime = Date.now();
      } else {
        // Page became visible - resume normal sleep detection
        if (pageHiddenTime) {
          const hiddenDuration = Date.now() - pageHiddenTime;
          if (hiddenDuration > pauseAfterHidden) {
            // Page was hidden for extended period, resume checking immediately
            clearTimeout(sleepCheckInterval);
            currentInterval = minInterval;
            scheduleNextCheck();
          }
        }
        pageHiddenTime = null;
        updateLastActivity();
      }
    };

    // Start initial check
    scheduleNextCheck();

    // Monitor user activity to adjust timer frequency
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateLastActivity, { passive: true });
    });

    // Monitor visibility for battery optimization
    document.addEventListener('visibilitychange', handleVisibilityForSleep);

    return () => {
      clearTimeout(sleepCheckInterval);
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateLastActivity);
      });
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityForSleep
      );
    };
  }, [handleSystemWake]);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const lifecycleNavigator = navigator as LifecycleNavigator;

    // Core visibility detection
    document.addEventListener(
      'visibilitychange',
      throttledHandleVisibilityChange
    );

    // Window focus/blur detection
    window.addEventListener('focus', throttledHandleFocus);
    window.addEventListener('blur', throttledHandleBlur);

    // Page show/hide detection (crucial for browsers with back-forward cache)
    window.addEventListener('pageshow', throttledHandleVisibilityChange);
    window.addEventListener('pagehide', throttledHandlePageHide);

    // Mobile orientation changes (often indicates app returning to foreground)
    if ('orientation' in window) {
      window.addEventListener(
        'orientationchange',
        throttledHandleOrientationChange
      );
    }

    // Page Lifecycle API events (modern mobile browsers)
    if ('onfreeze' in document) {
      document.addEventListener('freeze', throttledHandleFreeze);
      document.addEventListener('resume', throttledHandleResume);
    }

    // Enhanced network detection
    window.addEventListener('online', throttledHandleNetworkOnline);

    // Network Information API (connection type changes)
    const { connection } = lifecycleNavigator;
    if (connection) {
      connection.addEventListener('change', throttledHandleNetworkChange);
    }

    // iOS Safari specific: detect when adding to home screen
    if (lifecycleNavigator.standalone === false) {
      // Web app that could be added to home screen
      window.addEventListener(
        'beforeinstallprompt',
        throttledHandleVisibilityChange
      );
    }

    // PWA app state changes
    if ('serviceWorker' in navigator) {
      // PWA visibility changes when app becomes active/inactive
      document.addEventListener(
        'visibilitychange',
        throttledHandleVisibilityChange
      );
    }

    return () => {
      document.removeEventListener(
        'visibilitychange',
        throttledHandleVisibilityChange
      );
      window.removeEventListener('focus', throttledHandleFocus);
      window.removeEventListener('blur', throttledHandleBlur);
      window.removeEventListener('pageshow', throttledHandleVisibilityChange);
      window.removeEventListener('pagehide', throttledHandlePageHide);
      if ('orientation' in window) {
        window.removeEventListener(
          'orientationchange',
          throttledHandleOrientationChange
        );
      }
      if ('onfreeze' in document) {
        document.removeEventListener('freeze', throttledHandleFreeze);
        document.removeEventListener('resume', throttledHandleResume);
      }
      window.removeEventListener('online', throttledHandleNetworkOnline);

      // Network Information API cleanup
      if (connection) {
        connection.removeEventListener('change', throttledHandleNetworkChange);
      }

      // iOS Safari cleanup
      if (lifecycleNavigator.standalone === false) {
        window.removeEventListener(
          'beforeinstallprompt',
          throttledHandleVisibilityChange
        );
      }

      // Cancel any pending throttled calls
      throttledHandleVisibilityChange.cancel();
      throttledHandleFocus.cancel();
      throttledHandleBlur.cancel();
      throttledHandlePageHide.cancel();
      throttledHandleOrientationChange.cancel();
      throttledHandleFreeze.cancel();
      throttledHandleResume.cancel();
      throttledHandleNetworkOnline.cancel();
      throttledHandleNetworkChange.cancel();
    };
  }, [
    throttledHandleVisibilityChange,
    throttledHandleFocus,
    throttledHandleBlur,
    throttledHandlePageHide,
    throttledHandleOrientationChange,
    throttledHandleFreeze,
    throttledHandleResume,
    throttledHandleNetworkOnline,
    throttledHandleNetworkChange,
  ]);

  return { isVisible };
};
